import { AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface ConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  plaintiff: string;
}

export function ConflictModal({ isOpen, onClose, caseId, plaintiff }: ConflictModalProps) {
  const [status, setStatus] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateReport = async () => {
    if (!status) {
      toast.error('Please select a conflict status');
      return;
    }

    setIsGenerating(true);

    // Simulate report generation
    setTimeout(() => {
      setIsGenerating(false);
      toast.success('Conflict report generated and emailed to attorney');
      onClose();
      // Reset form
      setStatus('');
      setNotes('');
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Record Conflict Status
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Case Info */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-600 text-sm">Case</p>
            <p className="text-gray-900 font-medium">{plaintiff}</p>
            <p className="text-gray-500 text-xs">ID: {caseId}</p>
          </div>

          {/* Status Selection */}
          <div className="space-y-2">
            <Label className="text-gray-700">Conflict Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                <SelectValue placeholder="Select conflict status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 shadow-lg">
                <SelectItem
                  value="cleared"
                  className="text-gray-900 focus:bg-gray-100 focus:text-gray-900"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Cleared - No Conflict</span>
                  </div>
                </SelectItem>
                <SelectItem
                  value="conflict"
                  className="text-gray-900 focus:bg-gray-100 focus:text-gray-900"
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span>Conflict - Cannot Proceed</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label className="text-gray-700">Additional Notes (Optional)</Label>
            <Textarea
              placeholder="Add any additional context or reasoning..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-[#A66B32] focus:ring-[#A66B32]/30/20 resize-none"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateReport}
              disabled={isGenerating}
              className="flex-1 bg-[#A66B32] text-white hover:bg-[#241610] disabled:opacity-50 shadow-sm"
            >
              {isGenerating ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Generating...
                </div>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
